/* QR display — reusable for PHONE_FIRST checkout, donations, and account pending.
 *
 * Enterprise QR (Wave 5): prefer server PNG proxies over client SPAYD:
 *   - Signed-in / account pending: GET /customer-payments/:paymentId/qr
 *   - Guest checkout: GET /customer-checkout/sessions/:publicId/qr
 *   - Fallback: qrCodePayload from start-qr (legacy)
 *
 * start-monitoring runs on mount for all bank-transfer flows (prepay/donation/product).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { isCurrencyCode, type AnalyticsFlow, type CurrencyCode } from 'pi-kiosk-shared';
import { useCustomerShellChrome } from '../../app/CustomerShellChromeContext.js';
import { CUSTOMER_TOP_BAR_CLEARANCE_PT_CLASS, CUSTOMER_CONTENT_NARROW_CLASS } from '../../app/customerTopBarStyles.js';
import { Card } from '../../shared/ui/Card.js';
import { Button } from '../../shared/ui/Button.js';
import { PostPayCta } from '../post-pay/PostPayCta.js';
import { RefundRequestCta } from './RefundRequestCta.js';
import { resetShopCart } from '../shop/cart/resetShopCart.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import {
  type CheckoutSessionDTO,
  useGetCheckoutSession,
  usePaymentStatus,
  useStartPaymentMonitoring,
  useStopPaymentMonitoring} from './checkoutApi.js';
import { PostPurchaseFavoritePrompt } from './PostPurchaseFavoritePrompt.js';
import { readLastKnownSalesPointId, readLastKnownSalesPointSlug } from '../../shared/session/lastKnownSalesPoint.js';
import { GuestReceiptRecoveryBlock } from './GuestReceiptRecoveryBlock.js';
import { FIO_PAYMENT_STATUS_POLL_MS } from './paymentPollPolicy.js';
import {
  checkoutSessionQrPath,
  customerPaymentQrPath,
  fetchCustomerQrImageBlob,
  platformCustomerPaymentQrPath} from './paymentQrDownload.js';
import {
  canSharePaymentQrFile,
  canTriggerPngBlobDownload,
  dataUrlToPngBlob,
  pngBlobToFile,
  sharePaymentQrFile,
  triggerPngBlobDownload} from './paymentQrShare.js';
import { useCustomerAnalytics } from '../../shared/analytics/CustomerAnalyticsProvider.js';
import { emitCustomerErrorShown } from '../../shared/analytics/customerErrorAnalytics.js';
import {
  ANALYTICS_UNIVERSAL_EVENTS,
  ANALYTICS_FUNNEL_EVENTS} from 'pi-kiosk-shared';
import { buildQrDisplayedMetadata } from 'pi-kiosk-shared/analytics/metadataBuilders';
import { useLinkAccountToTransaction } from '../auth/authApi.js';
import { SignInScreen } from '../auth/SignInScreen.js';
import { SignedInAccountPromptCard } from '../auth/components/SignedInAccountPromptCard.js';
import { canMountEmbeddedSignInForm } from '../auth/routing/canMountEmbeddedSignInForm.js';
import {
  isTransactionLinkedToOtherCustomerError,
  linkAccountIdempotencyKey} from '../post-pay/linkAccountHelpers.js';
import { parkPostPayAuthReturnTarget } from '../post-pay/parkPostPayAuthReturn.js';
import {
  beginPostPayAuthClaimAndClearPending,
  clearPendingPostPayAuthAction,
  endPostPayAuthClaimInFlight,
  isPostPayAuthClaimGenerationCurrent,
  recordPostPayAuthActionOutcome,
  savePendingPostPayLinkClaim,
} from '../post-pay/postPayPendingAuthAction.js';
import { useHasSucceededPostPayAuthOutcome } from '../post-pay/usePostPayAuthActionOutcome.js';
import { toCustomerFacingErrorMessage } from '../../shared/errors/toCustomerFacingErrorMessage.js';
import {
  useCheckoutKioskSSE,
  useManualReconciliationCheck,
  usePaymentTimer} from './hooks/index.js';
import { usePaymentPendingScope } from '../../app/pwa/usePaymentPendingScope.js';
import { ProviderIcon } from '../../shared/ui/ProviderIcon.js';
import {
  CUSTOMER_BANK_TRANSFER_PROVIDER_ID,
  CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH,
  customerPaymentProviderTitle} from './paymentProvidersApi.js';
import { qrLog } from './logging.js';
import {
  isGloballySignedInChrome,
  isSignedInNavChrome} from '../hub/customerHubPaths.js';
import { useOptionalOverlayBackDismiss } from '../../shared/context/OverlayBackDismissContext.js';
import { showToast } from '../../shared/ui/toastStore.js';
import { useLeavePaymentSurfaceOnAuthLoss } from './useLeavePaymentSurfaceOnAuthLoss.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';

type QrLoadPhase = 'loading' | 'ready' | 'denied' | 'error';

interface QrPaymentViewProps {
  paymentId: string;
  amountMinor?: number;
  currency?: string;
  /** Checkout session `publicId` from route or create-session response. */
  checkoutSessionId?: string | null;
  /** Legacy SPAYD from start-qr — used only when PNG proxy is unavailable. */
  qrCodePayload?: string | null;
  /**
   * Explicit allow from start-qr. Omitted / null / `false` all try-load until
   * polled status sets `qrVisible: false`. Polled `true` always wins; polled
   * `false` is the only access deny. Prop `false` must not flash qrNotAvailable
   * before the first status poll (stale unpaid-reuse hide DTO).
   */
  qrVisible?: boolean | null;
  /** Clears persisted shop cart for `(tenant, kiosk)` once payment is COMPLETED. */
  cartClearKioskId?: number | null;
  /** Optional Order # from caller (session); status poll also supplies it. */
  transactionId?: number | null;
  analyticsFlow?: AnalyticsFlow;
  onCancel?: () => void;
  onContinue?: () => void;
  /**
   * Wave E / G2 — fetch via absolute `/api/v1/customer-account/payments/:id/qr`
   * (platform ownership; never `/api/__platform__/…`).
   */
  platformQr?: boolean;
  /**
   * Row-tenant slug for tenant-prefixed QR URL after switch (non-platform).
   * Defaults to chrome `useTenantCode()`.
   */
  apiTenantCode?: string;
}

const FIO_CHECK_INTERVAL_SECONDS = FIO_PAYMENT_STATUS_POLL_MS / 1000;

/**
 * G11 — require a real currency from props / status / checkout session.
 * Never invent CZK when the charge currency is absent (EUR sessions must fail-closed).
 */
function resolveQrDisplayCurrency(
  ...candidates: ReadonlyArray<string | null | undefined>
): CurrencyCode | null {
  for (const raw of candidates) {
    if (typeof raw !== 'string') {
      continue;
    }
    const normalized = raw.trim().toUpperCase();
    if (isCurrencyCode(normalized)) {
      return normalized;
    }
  }
  return null;
}

export function QrPaymentView({
  paymentId,
  amountMinor: amountMinorProp,
  currency: currencyProp,
  checkoutSessionId,
  qrCodePayload = null,
  qrVisible: qrVisibleProp = null,
  cartClearKioskId = null,
  transactionId: transactionIdProp = null,
  analyticsFlow = 'RETAIL',
  onCancel,
  onContinue,
  platformQr: platformQrProp = false,
  apiTenantCode: apiTenantCodeProp}: QrPaymentViewProps): JSX.Element {
  const { t } = useTranslation('customer');
  const underShellChrome = useCustomerShellChrome();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  const platformChrome = isPlatformChromeTenant(tenantCode);
  const qrApiTenantCode =
    apiTenantCodeProp !== undefined && apiTenantCodeProp.trim().length > 0
      ? apiTenantCodeProp.trim()
      : tenantCode;
  const paymentApiTenant = hasCustomerApiTenant(qrApiTenantCode)
    ? qrApiTenantCode
    : null;
  // Row-tenant account QR (`?tenant=`) must use tenant-scoped customer-payments,
  // even under platform chrome. Only fall back to absolute ownership QR when
  // there is no usable api tenant (platformQr / bare hub).
  const usePlatformQr =
    paymentApiTenant === null && (platformQrProp || platformChrome);
  const location = useLocation();
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const signedInChrome = isSignedInNavChrome({ session });
  const canMountSignIn = canMountEmbeddedSignInForm({ session });
  const { withholdPaymentChrome } = useLeavePaymentSurfaceOnAuthLoss({
    tenantCode,
    surfaceKey: paymentId});
  const linkAccount = useLinkAccountToTransaction();
  const startMonitoring = useStartPaymentMonitoring();
  const stopMonitoring = useStopPaymentMonitoring();
  const status = usePaymentStatus(paymentId, FIO_PAYMENT_STATUS_POLL_MS, {
    tenantCode: paymentApiTenant ?? undefined,
    enabled: paymentApiTenant !== null,
  });
  const needsDerivedPaymentDetails =
    amountMinorProp === undefined || currencyProp === undefined || checkoutSessionId === undefined;
  const resolvedAmountMinor = amountMinorProp ?? status.data?.amountMinor ?? 0;
  const resolvedCheckoutSessionId =
    checkoutSessionId ?? status.data?.checkoutSessionPublicId ?? null;
  const checkoutSessionQuery = useGetCheckoutSession(resolvedCheckoutSessionId, 'active');
  // G11 — props → status poll → checkout session; no silent CZK invent.
  const resolvedCurrency = resolveQrDisplayCurrency(
    currencyProp,
    status.data?.currency,
    checkoutSessionQuery.data?.currency,
  );
  const favoritePromptSalesPointId =
    cartClearKioskId ??
    checkoutSessionQuery.data?.salesPointId ??
    readLastKnownSalesPointId(tenantCode);
  const resolvedOrderNumber = ((): string | null => {
    const fromProp =
      transactionIdProp !== null &&
      transactionIdProp !== undefined &&
      Number.isFinite(transactionIdProp) &&
      transactionIdProp > 0
        ? transactionIdProp
        : null;
    const fromStatus = status.data?.transactionId;
    const fromStatusNormalized =
      fromStatus !== null &&
      fromStatus !== undefined &&
      Number.isFinite(fromStatus) &&
      fromStatus > 0
        ? fromStatus
        : null;
    const orderId = fromProp ?? fromStatusNormalized;
    return orderId !== null ? String(orderId) : null;
  })();
  const analytics = useCustomerAnalytics();
  const manualCheck = useManualReconciliationCheck({
    paymentId,
    checkoutSessionId: resolvedCheckoutSessionId,
    tenantCode: paymentApiTenant,
  });
  const payStatus = status.data?.status ?? 'PENDING';
  const isCompleted = payStatus === 'COMPLETED';
  const isFailed = payStatus === 'FAILED';
  const isExpired = payStatus === 'EXPIRED';
  const [paymentTimeoutReceived, setPaymentTimeoutReceived] = useState(false);
  const isAwaitingCompletion = manualCheck.checkPhase === 'completing';
  const showTimeoutRecovery =
    (isExpired || paymentTimeoutReceived) &&
    !isAwaitingCompletion &&
    (manualCheck.checkPhase === 'exhausted' || manualCheck.customerAssertedPaid);
  const isTerminalFailed =
    isFailed || (isExpired && !showTimeoutRecovery && !isAwaitingCompletion);
  // G3 — mirror CheckoutComplete: do not keep pending-scope armed while leaving on auth loss
  // (withhold clears/suppresses checkpoint; active scope would allow+rewrite after leave).
  const isPaymentPending =
    payStatus === 'PENDING' && !showTimeoutRecovery && !withholdPaymentChrome;
  const refundEligible = status.data?.refundEligible === true;
  const paymentPendingCheckpoint = useMemo(
    () =>
      isPaymentPending
        ? {
            returnTo: `${location.pathname}${location.search}`,
            paymentId,
            journey: analyticsFlow}
        : undefined,
    [analyticsFlow, isPaymentPending, location.pathname, location.search, paymentId],
  );
  usePaymentPendingScope(isPaymentPending, paymentPendingCheckpoint);
  const cartClearedAfterPayRef = useRef(false);
  const checkoutSessionCacheSyncedRef = useRef(false);
  const sessionCompletedRef = useRef(false);
  const sessionAbandonedRef = useRef(false);
  const terminalFailureShownRef = useRef(false);

  const effectiveQrVisible = ((): boolean => {
    // Polled status wins: a stale start-qr / resume stub must not keep hiding
    // an unpaid QR after the customer backs out and returns.
    if (status.data?.qrVisible === true) {
      return true;
    }
    // Real deny only after poll. Prop `false` must not deny before status loads
    // (unpaid-reuse DTO may stale-hide; try-load PNG/SPAYD until poll confirms).
    if (status.data?.qrVisible === false) {
      return false;
    }
    if (qrVisibleProp === true) {
      return true;
    }
    // Status not loaded / qrVisible omitted: try-load even if qrVisibleProp === false.
    return true;
  })();

  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [qrLoadPhase, setQrLoadPhase] = useState<QrLoadPhase>('loading');
  /** Bumped by Retry and by polls that keep qrVisible true while load is error/denied/empty. */
  const [qrLoadNonce, setQrLoadNonce] = useState(0);
  const qrLoadPhaseRef = useRef(qrLoadPhase);
  const qrImageUrlRef = useRef(qrImageUrl);
  useEffect(() => {
    qrLoadPhaseRef.current = qrLoadPhase;
  }, [qrLoadPhase]);
  useEffect(() => {
    qrImageUrlRef.current = qrImageUrl;
  }, [qrImageUrl]);
  const qrBlobUrlRef = useRef<string | null>(null);
  /** Retained PNG blob for Web Share / desktop download (MOB-04). */
  const qrBlobRef = useRef<Blob | null>(null);
  const previewObjectUrlRef = useRef<string | null>(null);
  const [qrPreviewOpen, setQrPreviewOpen] = useState(false);
  const [qrPreviewImageUrl, setQrPreviewImageUrl] = useState<string | null>(null);
  const [shareBusy, setShareBusy] = useState(false);
  const overlayBack = useOptionalOverlayBackDismiss();
  const setOverlayDismiss = overlayBack?.setOverlayDismiss;

  const revokePreviewObjectUrl = useCallback((): void => {
    const current = previewObjectUrlRef.current;
    if (current !== null) {
      URL.revokeObjectURL(current);
    }
    previewObjectUrlRef.current = null;
    setQrPreviewImageUrl(null);
  }, []);

  const closeQrPreview = useCallback((): void => {
    setQrPreviewOpen(false);
    revokePreviewObjectUrl();
  }, [revokePreviewObjectUrl]);

  const openQrPreview = useCallback(
    (blob: Blob): void => {
      revokePreviewObjectUrl();
      const objectUrl = URL.createObjectURL(blob);
      previewObjectUrlRef.current = objectUrl;
      setQrPreviewImageUrl(objectUrl);
      setQrPreviewOpen(true);
    },
    [revokePreviewObjectUrl],
  );

  useEffect(() => {
    if (!qrPreviewOpen || setOverlayDismiss === undefined) {
      return;
    }
    setOverlayDismiss(() => {
      closeQrPreview();
    });
    return (): void => {
      setOverlayDismiss(null);
    };
  }, [closeQrPreview, qrPreviewOpen, setOverlayDismiss]);

  const revokeQrBlobUrl = useCallback((): void => {
    const current = qrBlobUrlRef.current;
    if (current !== null && current.startsWith('blob:')) {
      URL.revokeObjectURL(current);
    }
    qrBlobUrlRef.current = null;
    // Keep qrBlobRef — Blob outlives the object URL and is needed for share/download.
  }, []);

  useEffect(() => {
    return (): void => {
      revokePreviewObjectUrl();
    };
  }, [revokePreviewObjectUrl]);

  useEffect(() => {
    if (isCompleted && !sessionCompletedRef.current) {
      sessionCompletedRef.current = true;
      void analytics.completeSession();
    }
  }, [analytics, isCompleted]);

  useEffect(() => {
    if (!isTerminalFailed || terminalFailureShownRef.current) {
      return;
    }
    terminalFailureShownRef.current = true;
    // Plan §5 — `payment_failed` is server-emitted; client emits `error_shown`
    // when the customer sees the terminal failure UI (see docs/ANALYTICS_SERVER_EMITTED_EVENTS.md).
    // Await emit before abandon — track is async and abandon clears session auth.
    void (async (): Promise<void> => {
      await emitCustomerErrorShown({
        track: analytics.track,
        flow: analyticsFlow,
        screenName: 'checkout_qr',
        screen: 'checkout_qr',
        failureReason: payStatus === 'EXPIRED' ? 'payment_expired' : 'payment_failed',
        message: t('qrPayment.paymentFailed'),
      });
      if (!sessionAbandonedRef.current) {
        sessionAbandonedRef.current = true;
        await analytics.abandonSession(
          payStatus === 'EXPIRED' ? 'payment_expired' : 'payment_failed',
        );
      }
    })();
  }, [analytics, analyticsFlow, isTerminalFailed, payStatus, t]);

  useEffect(() => {
    if (!needsDerivedPaymentDetails || !status.isError) {
      return;
    }
    qrLog.error('Payment status load failed', status.error, {
      tenantCode,
      paymentId,
      operation: 'paymentStatus'});
  }, [needsDerivedPaymentDetails, paymentId, status.error, status.isError, tenantCode]);

  const [downloadHint, setDownloadHint] = useState<string | null>(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [recoveryLinked, setRecoveryLinked] = useState(false);
  // G1/G3 — live outcome sync when runner settles while QR chrome is already mounted.
  const outcomeLinked = useHasSucceededPostPayAuthOutcome({
    kind: 'link-account',
    paymentId,
  });
  const showRecoveryLinked = recoveryLinked || outcomeLinked;
  const [monitoringStartTime, setMonitoringStartTime] = useState<number | null>(null);
  const monitoringStartedRef = useRef(false);
  const monitoringStoppedRef = useRef(false);
  const autoLinkAttemptedRef = useRef(false);
  // Mutation objects change identity every render — hold mutateAsync in refs so the
  // FIO start/stop effects do not re-run (start→immediate-stop) on identity churn.
  const startMonitoringMutateAsyncRef = useRef(startMonitoring.mutateAsync);
  const stopMonitoringMutateAsyncRef = useRef(stopMonitoring.mutateAsync);
  useLayoutEffect(() => {
    startMonitoringMutateAsyncRef.current = startMonitoring.mutateAsync;
    stopMonitoringMutateAsyncRef.current = stopMonitoring.mutateAsync;
  }, [startMonitoring.mutateAsync, stopMonitoring.mutateAsync]);

  // Account QR / donation pay omit cartClearKioskId — never open `/events/undefined`.
  const { isConnected } = useCheckoutKioskSSE(
    cartClearKioskId != null && cartClearKioskId > 0 ? cartClearKioskId : null,
  );
  // Access policy only — PNG/SPAYD fetch failure is `error` and must still show the panel.
  const showQrPanel = effectiveQrVisible;
  const retryQrImageLoad = useCallback((): void => {
    setQrLoadNonce((n) => n + 1);
  }, []);
  const { timeUntilNextCheck, isChecking } = usePaymentTimer({
    showTimer: showQrPanel,
    checkInterval: FIO_CHECK_INTERVAL_SECONDS,
    monitoringStartTime,
    paymentId});

  // Reset sticky flags when the monitoring key changes (before start effect below).
  useEffect(() => {
    monitoringStartedRef.current = false;
    monitoringStoppedRef.current = false;
  }, [paymentId, paymentApiTenant]);

  useEffect(() => {
    if (paymentApiTenant === null) {
      return;
    }
    if (monitoringStartedRef.current) {
      return;
    }
    monitoringStartedRef.current = true;
    let isUnmounted = false;
    void startMonitoringMutateAsyncRef
      .current({ paymentId, tenantCode: paymentApiTenant })
      .then((response) => {
        if (!isUnmounted) {
          setMonitoringStartTime(response.monitoringStartTime ?? null);
        }
      })
      .catch((error: unknown) => {
        qrLog.warn('Payment monitoring start failed', {
          tenantCode: paymentApiTenant,
          paymentId,
          reason: error instanceof Error ? error.name : 'unknown'});
        if (!isUnmounted) {
          setMonitoringStartTime(null);
        }
      });
    return (): void => {
      isUnmounted = true;
      if (monitoringStoppedRef.current) {
        return;
      }
      monitoringStoppedRef.current = true;
      void stopMonitoringMutateAsyncRef
        .current({ paymentId, tenantCode: paymentApiTenant })
        .catch((error: unknown) => {
          qrLog.warn('Payment monitoring stop failed', {
            tenantCode: paymentApiTenant,
            paymentId,
            reason: error instanceof Error ? error.name : 'unknown'});
        });
    };
  }, [paymentApiTenant, paymentId]);

  useEffect(() => {
    if (paymentApiTenant === null) {
      return;
    }
    if (monitoringStoppedRef.current) {
      return;
    }
    if (isCompleted || isTerminalFailed) {
      monitoringStoppedRef.current = true;
      void stopMonitoringMutateAsyncRef
        .current({ paymentId, tenantCode: paymentApiTenant })
        .catch((error: unknown) => {
          qrLog.warn('Payment monitoring stop failed', {
            tenantCode: paymentApiTenant,
            paymentId,
            reason: error instanceof Error ? error.name : 'unknown'});
        });
    }
  }, [isCompleted, isTerminalFailed, paymentApiTenant, paymentId]);

  useEffect(() => {
    if (isCompleted && cartClearKioskId !== null && !cartClearedAfterPayRef.current) {
      cartClearedAfterPayRef.current = true;
      resetShopCart({
        tenantCode,
        kioskId: cartClearKioskId,
        source: 'qr-payment-completed'});
    }
  }, [isCompleted, cartClearKioskId, tenantCode]);

  // G1 — payment COMPLETED while checkout-session cache still AWAITING_PAYMENT (staleTime 10s).
  // Prefer prop session id (PhoneFirstCheckoutQrStepView); fall back to status-derived publicId.
  // Do not mark synced when cache entries are still null — retry once session query hydrates.
  const fromPropSessionId =
    typeof checkoutSessionId === 'string' && checkoutSessionId.trim().length > 0
      ? checkoutSessionId.trim()
      : null;
  const fromResolvedSessionId =
    resolvedCheckoutSessionId !== null && resolvedCheckoutSessionId.length > 0
      ? resolvedCheckoutSessionId
      : null;
  const cacheSyncSessionId = fromPropSessionId ?? fromResolvedSessionId;
  const checkoutSessionCacheHydrated = useSyncExternalStore(
    (onStoreChange) => queryClient.getQueryCache().subscribe(onStoreChange),
    () => {
      if (cacheSyncSessionId === null) {
        return false;
      }
      const queryKey = customerQueryKeys.checkoutSession(tenantCode, cacheSyncSessionId);
      return queryClient
        .getQueriesData<CheckoutSessionDTO>({ queryKey })
        .some(([, data]) => data != null);
    },
    () => false,
  );
  useEffect(() => {
    if (!isCompleted || checkoutSessionCacheSyncedRef.current) {
      return;
    }
    if (cacheSyncSessionId === null) {
      return;
    }
    const queryKey = customerQueryKeys.checkoutSession(tenantCode, cacheSyncSessionId);
    let applied = false;
    queryClient.setQueriesData<CheckoutSessionDTO>({ queryKey }, (prev) => {
      if (prev == null) {
        return prev;
      }
      applied = true;
      if (prev.status === 'COMPLETED') {
        return prev;
      }
      return { ...prev, status: 'COMPLETED' };
    });
    if (!applied) {
      // Empty cache — leave synced false; checkoutSessionCacheHydrated re-triggers on hydrate.
      return;
    }
    checkoutSessionCacheSyncedRef.current = true;
    void queryClient.invalidateQueries({ queryKey });
  }, [
    cacheSyncSessionId,
    checkoutSessionCacheHydrated,
    isCompleted,
    queryClient,
    tenantCode,
  ]);

  useEffect(() => {
    const handleRealtimePaymentUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ data?: string }>;
      const raw = customEvent.detail?.data;
      if (typeof raw !== 'string' || raw.length === 0) {
        return;
      }
      try {
        const parsed = JSON.parse(raw) as {
          type?: string;
          updateType?: string;
          data?: {
            paymentId?: string;
            checkTime?: number;
            status?: string;
            transactionId?: number;
          };
        };

        // Kiosk back-button cancel path — BroadcastService.broadcastTransactionUpdate
        // (type transaction_update, not product_update).
        if (parsed.type === 'transaction_update') {
          const eventPaymentId = parsed.data?.paymentId;
          if (eventPaymentId !== paymentId) {
            return;
          }
          const terminalStatus = parsed.data?.status;
          if (
            terminalStatus === 'CANCELLED' ||
            terminalStatus === 'COMPLETED' ||
            terminalStatus === 'FAILED' ||
            terminalStatus === 'TIMEOUT'
          ) {
            void status.refetch();
          }
          return;
        }

        if (parsed.type !== 'product_update') {
          return;
        }
        const eventPaymentId = parsed.data?.paymentId;
        if (eventPaymentId !== paymentId) {
          return;
        }
        if (parsed.updateType === 'payment_check_started') {
          window.dispatchEvent(
            new CustomEvent('payment-check-started', {
              detail: {
                paymentId: eventPaymentId,
                checkTime: parsed.data?.checkTime ?? Date.now()}})
          );
          return;
        }
        if (
          parsed.updateType === 'payment_completed' ||
          parsed.updateType === 'payment_cancelled' ||
          parsed.updateType === 'payment_timeout' ||
          // Server-emitted via BroadcastService.broadcastPaymentFailed (CancelIntent / GatewayNotify).
          parsed.updateType === 'payment_failed'
        ) {
          if (parsed.updateType === 'payment_timeout') {
            setPaymentTimeoutReceived(true);
          }
          void status.refetch();
        }
      } catch {
        return;
      }
    };

    window.addEventListener('websocket-message', handleRealtimePaymentUpdate);
    return (): void => {
      window.removeEventListener('websocket-message', handleRealtimePaymentUpdate);
    };
  }, [paymentId, status]);

  // Bank-app handoff: tab is hidden → timers/polls pause. On return, force a status
  // refetch so COMPLETED (often already finished server-side) is not stuck behind a
  // frozen "next check" countdown. visibilitychange is more reliable than focus on iOS.
  const statusRefetchRef = useRef(status.refetch);
  useLayoutEffect(() => {
    statusRefetchRef.current = status.refetch;
  }, [status.refetch]);
  useEffect(() => {
    if (isCompleted || isTerminalFailed) {
      return;
    }
    const refetchOnTabReturn = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      void statusRefetchRef.current();
    };
    document.addEventListener('visibilitychange', refetchOnTabReturn);
    window.addEventListener('pageshow', refetchOnTabReturn);
    window.addEventListener('focus', refetchOnTabReturn);
    return (): void => {
      document.removeEventListener('visibilitychange', refetchOnTabReturn);
      window.removeEventListener('pageshow', refetchOnTabReturn);
      window.removeEventListener('focus', refetchOnTabReturn);
    };
  }, [isCompleted, isTerminalFailed]);

  const qrDisplayedTrackedRef = useRef(false);
  useEffect(() => {
    if (qrLoadPhase !== 'ready' || qrDisplayedTrackedRef.current) {
      return;
    }
    qrDisplayedTrackedRef.current = true;
    analytics.track({
      eventName: ANALYTICS_FUNNEL_EVENTS.QR_DISPLAYED,
      flow: analyticsFlow,
      screenName: 'checkout_qr',
      paymentId,
      metadata: buildQrDisplayedMetadata({
        screenName: 'checkout_qr',
        paymentId,
        surface: 'customer'})});
  }, [analytics, analyticsFlow, paymentId, qrLoadPhase]);

  useEffect(() => {
    if (!effectiveQrVisible) {
      // Defer setState — react-hooks/set-state-in-effect forbids sync setState in effect body.
      const timer = window.setTimeout(() => {
        setQrLoadPhase('denied');
        closeQrPreview();
        revokeQrBlobUrl();
      }, 0);
      return (): void => {
        window.clearTimeout(timer);
      };
    }

    let cancelled = false;

    const loadQrImage = async (): Promise<void> => {
      setQrLoadPhase('loading');
      setQrImageUrl(null);
      closeQrPreview();
      revokeQrBlobUrl();
      qrBlobRef.current = null;

      const payload =
        typeof qrCodePayload === 'string' ? qrCodePayload.trim() : '';

      const renderPayloadFallback = async (): Promise<boolean> => {
        if (payload.length === 0) {
          return false;
        }
        try {
          const dataUrl = await QRCode.toDataURL(payload, {
            width: 320,
            margin: 2,
            errorCorrectionLevel: 'M'});
          if (cancelled) {
            return true;
          }
          try {
            qrBlobRef.current = await dataUrlToPngBlob(dataUrl);
          } catch (blobError: unknown) {
            qrLog.warn('QR data URL to Blob conversion failed', {
              tenantCode,
              paymentId,
              operation: 'dataUrlToPngBlob',
              reason: blobError instanceof Error ? blobError.name : 'unknown'});
            qrBlobRef.current = null;
          }
          if (cancelled) {
            return true;
          }
          setQrImageUrl(dataUrl);
          setQrLoadPhase('ready');
          return true;
        } catch (error: unknown) {
          qrLog.error('QR payload render failed', error, {
            tenantCode,
            paymentId,
            operation: 'renderPayloadFallback'});
          if (!cancelled) {
            setQrLoadPhase('error');
          }
          return true;
        }
      };

      if (await renderPayloadFallback()) {
        return;
      }

      const sessionPublicId =
        resolvedCheckoutSessionId !== null && resolvedCheckoutSessionId.length > 0
          ? resolvedCheckoutSessionId
          : null;

      const switchReady = !usePlatformQr && isGloballySignedInChrome({ session });

      const canFetchPaymentQr =
        usePlatformQr
          ? isGloballySignedInChrome({ session })
          : switchReady;
      if (canFetchPaymentQr) {
        try {
          const blob = await fetchCustomerQrImageBlob({
            apiBaseUrl,
            tenantCode: usePlatformQr ? tenantCode : qrApiTenantCode,
            path: usePlatformQr
              ? platformCustomerPaymentQrPath(paymentId)
              : customerPaymentQrPath(paymentId),
            accessToken: null,
            ...(usePlatformQr ? { platformAbsolute: true } : {})});
          if (cancelled) {
            return;
          }
          qrBlobRef.current = blob;
          const objectUrl = URL.createObjectURL(blob);
          qrBlobUrlRef.current = objectUrl;
          setQrImageUrl(objectUrl);
          setQrLoadPhase('ready');
          return;
        } catch (error: unknown) {
          qrLog.warn('Payment QR proxy fetch failed', {
            tenantCode,
            paymentId,
            operation: 'customerPaymentQr',
            reason: error instanceof Error ? error.name : 'unknown'});
          // Fall through to checkout-session proxy or SPAYD fallback.
        }
      }

      if (sessionPublicId !== null && !usePlatformQr) {
        try {
          // Guest / N10 pending: cookieless. After S1 ready: Bearer XOR cookie.
          const blob = await fetchCustomerQrImageBlob({
            apiBaseUrl,
            tenantCode: qrApiTenantCode,
            path: checkoutSessionQrPath(sessionPublicId),
            accessToken: null,
          });
          if (cancelled) {
            return;
          }
          qrBlobRef.current = blob;
          const objectUrl = URL.createObjectURL(blob);
          qrBlobUrlRef.current = objectUrl;
          setQrImageUrl(objectUrl);
          setQrLoadPhase('ready');
          return;
        } catch (error: unknown) {
          qrLog.warn('Checkout session QR proxy fetch failed', {
            tenantCode,
            paymentId,
            operation: 'checkoutSessionQr',
            reason: error instanceof Error ? error.name : 'unknown'});
          // Fall through to SPAYD fallback.
        }
      }

      if (await renderPayloadFallback()) {
        return;
      }

      // Proxies failed and no SPAYD — this is a load failure, not access deny.
      if (!cancelled) {
        setQrLoadPhase('error');
      }
    };

    void loadQrImage();

    return (): void => {
      cancelled = true;
      revokeQrBlobUrl();
    };
  }, [
    closeQrPreview,
    effectiveQrVisible,
    apiBaseUrl,
    resolvedCheckoutSessionId,
    paymentId,
    qrApiTenantCode,
    qrCodePayload,
    qrLoadNonce,
    revokeQrBlobUrl,
    session,
    status.data?.qrVisible,
    tenantCode,
    usePlatformQr,
  ]);

  const polledQrVisible = status.data?.qrVisible === true;
  const statusDataUpdatedAt = status.dataUpdatedAt;
  useEffect(() => {
    if (!polledQrVisible) {
      return;
    }
    const phase = qrLoadPhaseRef.current;
    const imageEmpty = qrImageUrlRef.current === null;
    const isEmpty = imageEmpty && phase !== 'loading' && phase !== 'ready';
    if (phase !== 'error' && phase !== 'denied' && !isEmpty) {
      return;
    }
    const timer = window.setTimeout(() => {
      setQrLoadNonce((n) => n + 1);
    }, 0);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [polledQrVisible, statusDataUpdatedAt, paymentId]);

  const qrDownloadFileName = `rpapp-payment-${paymentId}.png`;

  const resolveQrPngBlob = useCallback(async (): Promise<Blob | null> => {
    if (qrBlobRef.current !== null) {
      return qrBlobRef.current;
    }
    if (qrImageUrl !== null && qrImageUrl.startsWith('data:')) {
      try {
        const blob = await dataUrlToPngBlob(qrImageUrl);
        qrBlobRef.current = blob;
        return blob;
      } catch (error: unknown) {
        qrLog.error('QR data URL to Blob failed on share', error, {
          tenantCode,
          paymentId,
          operation: 'resolveQrPngBlob'});
      }
    }
    // Object URL from a prior proxy fetch — recover when ref was cleared (Strict Mode / remount).
    if (qrImageUrl !== null && qrImageUrl.startsWith('blob:')) {
      try {
        const response = await fetch(qrImageUrl);
        if (response.ok) {
          const blob = await response.blob();
          const pngBlob =
            blob.type === 'image/png' || blob.type.length === 0
              ? new Blob([blob], { type: 'image/png' })
              : blob;
          qrBlobRef.current = pngBlob;
          return pngBlob;
        }
      } catch (error: unknown) {
        qrLog.error('QR blob URL re-fetch failed on share', error, {
          tenantCode,
          paymentId,
          operation: 'resolveQrPngBlob'});
      }
    }
    // Last resort: rasterize the on-screen QR <img> (covers revoked blob: URLs still painted).
    try {
      const img = document.querySelector<HTMLImageElement>(
        'img[alt*="QR"], img[alt*="qr"], img[alt*="Qr"]',
      );
      if (img !== null && img.complete && img.naturalWidth > 0) {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx !== null) {
          ctx.drawImage(img, 0, 0);
          const raster = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((blob) => resolve(blob), 'image/png');
          });
          if (raster !== null) {
            qrBlobRef.current = raster;
            return raster;
          }
        }
      }
    } catch (error: unknown) {
      qrLog.error('QR canvas rasterize failed on share', error, {
        tenantCode,
        paymentId,
        operation: 'resolveQrPngBlob'});
    }
    return null;
  }, [paymentId, qrImageUrl, tenantCode]);

  const handleCancel = (): void => {
    void (async (): Promise<void> => {
      await analytics.track({
        eventName: ANALYTICS_UNIVERSAL_EVENTS.BACK_CLICKED,
        flow: analyticsFlow,
        screenName: 'checkout_qr',
        metadata: {
          screen_name: 'checkout_qr',
        },
      });
      if (!sessionAbandonedRef.current) {
        sessionAbandonedRef.current = true;
        await analytics.abandonSession('payment_cancelled');
      }
      onCancel?.();
    })();
  };

  const handleCopyOrderNumber = useCallback((): void => {
    if (resolvedOrderNumber === null) {
      return;
    }
    void (async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(resolvedOrderNumber);
        showToast({
          message: t('qrPayment.orderNumberCopied'),
          variant: 'success'});
      } catch {
        showToast({
          message: t('qrPayment.orderNumberCopyFailed'),
          variant: 'danger'});
      }
    })();
  }, [resolvedOrderNumber, t]);

  const handleSaveOrShare = useCallback((): void => {
    if (shareBusy) {
      return;
    }
    if (qrLoadPhase !== 'ready' || qrImageUrl === null) {
      setDownloadHint(t('qrPayment.downloadNotReady'));
      return;
    }

    setShareBusy(true);
    setDownloadHint(null);

    void (async (): Promise<void> => {
      try {
        const blob = await resolveQrPngBlob();
        if (blob === null) {
          setDownloadHint(t('qrPayment.fileGenerationFailed'));
          showToast({
            message: t('qrPayment.fileGenerationFailed'),
            variant: 'danger'});
          return;
        }

        let file: File;
        try {
          file = pngBlobToFile(blob, qrDownloadFileName);
        } catch (error: unknown) {
          qrLog.error('QR File construction failed', error, {
            tenantCode,
            paymentId,
            operation: 'pngBlobToFile'});
          setDownloadHint(t('qrPayment.fileGenerationFailed'));
          showToast({
            message: t('qrPayment.fileGenerationFailed'),
            variant: 'danger'});
          return;
        }

        if (canSharePaymentQrFile(file)) {
          try {
            const result = await sharePaymentQrFile({
              file,
              title: t('qrPayment.shareTitle')});
            if (result === 'cancelled') {
              setDownloadHint(null);
              return;
            }
            setDownloadHint(t('qrPayment.shareOpened'));
            return;
          } catch (error: unknown) {
            qrLog.error('QR navigator.share failed', error, {
              tenantCode,
              paymentId,
              operation: 'sharePaymentQrFile'});
            setDownloadHint(t('qrPayment.technicalError'));
            showToast({
              message: t('qrPayment.technicalError'),
              variant: 'danger'});
            openQrPreview(blob);
            return;
          }
        }

        setDownloadHint(t('qrPayment.shareUnsupported'));
        openQrPreview(blob);
      } catch (error: unknown) {
        qrLog.error('QR save/share failed', error, {
          tenantCode,
          paymentId,
          operation: 'handleSaveOrShare'});
        setDownloadHint(t('qrPayment.technicalError'));
        showToast({
          message: t('qrPayment.technicalError'),
          variant: 'danger'});
      } finally {
        setShareBusy(false);
      }
    })();
  }, [
    openQrPreview,
    paymentId,
    qrDownloadFileName,
    qrImageUrl,
    qrLoadPhase,
    resolveQrPngBlob,
    shareBusy,
    t,
    tenantCode,
  ]);

  const showDesktopPngDownload = canTriggerPngBlobDownload();

  const handleDesktopDownload = useCallback((): void => {
    if (!canTriggerPngBlobDownload()) {
      setDownloadHint(t('qrPayment.downloadUnavailable'));
      showToast({
        message: t('qrPayment.downloadUnavailable'),
        variant: 'danger'});
      return;
    }
    const blob = qrBlobRef.current;
    if (blob === null) {
      setDownloadHint(t('qrPayment.fileGenerationFailed'));
      return;
    }
    try {
      triggerPngBlobDownload(blob, qrDownloadFileName);
    } catch (error: unknown) {
      qrLog.error('QR programmatic download failed', error, {
        tenantCode,
        paymentId,
        operation: 'triggerPngBlobDownload'});
      setDownloadHint(t('qrPayment.technicalError'));
      showToast({
        message: t('qrPayment.technicalError'),
        variant: 'danger'});
    }
  }, [paymentId, qrDownloadFileName, t, tenantCode]);

  const handleLinkRecovery = useCallback((customerIdFromAuth?: number): void => {
    if (autoLinkAttemptedRef.current) {
      return;
    }
    // Prefer customerId from auth land — React session may still be null on onSignedIn.
    const customerId = customerIdFromAuth ?? session?.customerId;
    if (customerId === undefined) {
      return;
    }
    if (
      customerIdFromAuth === undefined &&
      !isSignedInNavChrome({ session })
    ) {
      return;
    }
    // Begin claim first; clear matching pending only on success (leave pending if runner owns claim).
    const generation = beginPostPayAuthClaimAndClearPending({
      kind: 'link-account',
      paymentId,
    });
    if (generation === null) {
      return;
    }
    autoLinkAttemptedRef.current = true;
    linkAccount.mutate(
      {
        paymentId,
        customerId,
        idempotencyKey: linkAccountIdempotencyKey(paymentId, customerId)},
      {
        onSuccess: () => {
          if (
            !isPostPayAuthClaimGenerationCurrent({
              kind: 'link-account',
              paymentId,
              generation,
            })
          ) {
            return;
          }
          clearPendingPostPayAuthAction();
          recordPostPayAuthActionOutcome({ kind: 'link-account', paymentId });
          endPostPayAuthClaimInFlight({ kind: 'link-account', paymentId });
          setRecoveryLinked(true);
        },
        onError: (error: unknown) => {
          if (
            !isPostPayAuthClaimGenerationCurrent({
              kind: 'link-account',
              paymentId,
              generation,
            })
          ) {
            return;
          }
          endPostPayAuthClaimInFlight({ kind: 'link-account', paymentId });
          qrLog.error('Link account to payment failed', error, {
            tenantCode,
            paymentId,
            operation: 'linkAccount'});
          autoLinkAttemptedRef.current = false;
        }}
    );
  }, [linkAccount, paymentId, session, tenantCode]);

  /** G1 — park return before embedded SignIn so Google/Apple OIDC remounts this surface. */
  const openRecoverySignIn = useCallback((): void => {
    parkPostPayAuthReturnTarget(
      `${location.pathname}${location.search}`,
      tenantCode,
    );
    // G4 — durable link-account claim for post-OIDC (global runner); do not gate on exhausted.
    const claimTenant =
      apiTenantCodeProp !== undefined && apiTenantCodeProp.trim().length > 0
        ? apiTenantCodeProp.trim()
        : tenantCode;
    if (hasCustomerApiTenant(claimTenant)) {
      savePendingPostPayLinkClaim({ paymentId, tenantCode: claimTenant });
    }
    setShowSignIn(true);
  }, [
    apiTenantCodeProp,
    location.pathname,
    location.search,
    paymentId,
    tenantCode,
  ]);

  // G2 — runner may have claimed before remount; showRecoveryLinked derives from outcome.

  useEffect(() => {
    if (manualCheck.checkPhase !== 'exhausted') {
      return;
    }
    if (!signedInChrome || showRecoveryLinked) {
      return;
    }
    handleLinkRecovery();
  }, [handleLinkRecovery, manualCheck.checkPhase, showRecoveryLinked, signedInChrome]);

  const manualCheckMessage = ((): string | null => {
    if (manualCheck.messageKey === 'verifying_first') {
      return t('qrPayment.verifying_first');
    }
    if (manualCheck.messageKey === 'thank_you_still_looking') {
      return t('qrPayment.thank_you_still_looking');
    }
    if (manualCheck.messageKey === 'refund_promise') {
      return t('qrPayment.refund_promise');
    }
    return null;
  })();

  // G1 render gate: leave-on-auth-loss must not paint amount / QR / buttons / polling.
  if (withholdPaymentChrome) {
    return (
      <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
        <p
          className="p-4 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
          role="status"
          data-testid="qr-payment-auth-loss-leaving"
        >
          {t('account.sessionHydrating')}
        </p>
      </div>
    );
  }

  // While status polls (or is idle before first success), fall through to the
  // pending QR chrome so `customer-qr-box` (generating placeholder) can mount.
  // Only a hard status error withholds the panel.
  if (needsDerivedPaymentDetails && status.isError) {
    return (
      <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
        <Card padded>
          <div className="flex flex-col gap-[var(--vcl-space-stack-md)]">
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-danger)]" role="alert">
              {t('account.payments.qrLoadError')}
            </p>
            <Button
              intent="secondary"
              size="sm"
              onClick={() => {
                void status.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // G4 — SignIn from recovery must win over timeout-recovery / terminal chrome
  // so OTP mounts in-place after openRecoverySignIn (OIDC still uses park+pending).
  if (showSignIn && !signedInChrome) {
    if (!canMountSignIn) {
      return (
        <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
          <SignedInAccountPromptCard />
        </div>
      );
    }
    return (
      <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
        <SignInScreen
          heading={t('qrPayment.recoverySignInHeading')}
          helperText={t('qrPayment.recoverySignInHelper')}
          onSignedIn={handleLinkRecovery}
        />
      </div>
    );
  }

  if (showTimeoutRecovery && !isCompleted) {
    return (
      <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
      <Card padded>
        <div className="flex flex-col gap-[var(--vcl-space-stack-lg)]">
          <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]" role="status">
            {manualCheckMessage ?? t('qrPayment.recoveryDescription')}
          </p>
          <RefundRequestCta
            paymentId={paymentId}
            refundEligible={refundEligible}
            {...(paymentApiTenant !== null ? { apiTenantCode: paymentApiTenant } : {})}
          />
          {showRecoveryLinked ? (
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-success)]">{t('qrPayment.recoveryLinked')}</p>
          ) : null}
          {!showRecoveryLinked ? (
            (() => {
              if (signedInChrome) {
                return (
                  <Button
                    intent="secondary"
                    size="md"
                    block
                    onClick={() => {
                      handleLinkRecovery();
                    }}
                    disabled={linkAccount.isPending}
                  >
                    {linkAccount.isPending
                      ? t('qrPayment.recoveryLinking')
                      : t('qrPayment.recoveryLinkPayment')}
                  </Button>
                );
              }
              if (canMountSignIn) {
                return (
                  <Button intent="secondary" size="md" block onClick={openRecoverySignIn}>
                    {t('qrPayment.recoverySignIn')}
                  </Button>
                );
              }
              return <SignedInAccountPromptCard />;
            })()
          ) : null}
          {onCancel !== undefined ? (
            <Button intent="ghost" size="sm" onClick={handleCancel}>
              {t('qrPayment.back')}
            </Button>
          ) : null}
        </div>
      </Card>
      </div>
    );
  }

  if (isTerminalFailed) {
    return (
      <div className={CUSTOMER_CONTENT_NARROW_CLASS}>
      <Card padded>
        <div className="flex flex-col gap-[var(--vcl-space-stack-lg)]">
          <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-danger)]" role="alert">
            {t('qrPayment.paymentFailed')}
          </p>
          <RefundRequestCta
            paymentId={paymentId}
            refundEligible={refundEligible}
            {...(paymentApiTenant !== null ? { apiTenantCode: paymentApiTenant } : {})}
          />
          {onCancel !== undefined ? (
            <Button intent="ghost" size="sm" onClick={handleCancel}>
              {t('qrPayment.back')}
            </Button>
          ) : null}
        </div>
      </Card>
      </div>
    );
  }

  if (isCompleted) {
    const receiptRecovery = status.data?.receiptRecovery;
    return (
      <div className={`flex flex-col gap-[var(--vcl-space-stack-lg)] ${CUSTOMER_CONTENT_NARROW_CLASS}`} data-testid="customer-qr-payment-completed">
        <Card padded>
          <div className="flex flex-col items-center gap-[var(--vcl-space-stack-sm)] text-center">
            <span
              aria-hidden="true"
              className="text-3xl text-[var(--color-success)]"
            >
              ✓
            </span>
            <h2 className="text-lg font-semibold">{t('qrPayment.confirmed')}</h2>
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {t('qrPayment.thanksContinue')}
            </p>
          </div>
        </Card>
        {receiptRecovery != null ? (
          <GuestReceiptRecoveryBlock recovery={receiptRecovery} />
        ) : (
          <div className="flex flex-col items-center gap-[var(--vcl-space-stack-sm)]">
            <p
              className="text-[length:var(--vcl-type-body-sm-size)] text-center text-[var(--color-on-surface-muted)]"
              role="status"
              data-testid="customer-qr-receipt-preparing"
            >
              {t('qrPayment.receiptPreparing')}
            </p>
            <Button
              intent="secondary"
              size="sm"
              type="button"
              data-testid="customer-qr-receipt-preparing-retry"
              onClick={() => {
                const statusTenant = paymentApiTenant ?? tenantCode;
                void queryClient.invalidateQueries({
                  queryKey: customerQueryKeys.paymentStatus(statusTenant, paymentId),
                });
              }}
            >
              {t('qrPayment.retryLoad')}
            </Button>
          </div>
        )}
        {favoritePromptSalesPointId !== null &&
        favoritePromptSalesPointId !== undefined &&
        favoritePromptSalesPointId > 0 ? (
          <PostPurchaseFavoritePrompt
            tenantCode={tenantCode}
            salesPointId={favoritePromptSalesPointId}
            salesPointSlug={readLastKnownSalesPointSlug(tenantCode) ?? undefined}
            enabled={isCompleted}
          />
        ) : null}
        <PostPayCta
          paymentId={paymentId}
          cartClearKioskId={cartClearKioskId}
          onContinue={onContinue}
        />
      </div>
    );
  }

  return (
    <div className={`${CUSTOMER_CONTENT_NARROW_CLASS} min-w-0`}>
    <Card padded className="w-full min-w-0 [&_.rp-card-container-inner]:min-w-0">
      <div
        className="flex w-full min-w-0 flex-col items-center gap-[var(--vcl-space-stack-lg)] text-center"
        data-sse-connected={isConnected ? 'true' : 'false'}
      >
        <div className="flex items-center justify-center gap-[var(--vcl-gap-inline-sm)]">
          <ProviderIcon
            providerId={CUSTOMER_BANK_TRANSFER_PROVIDER_ID}
            size="lg"
            assetBasePath={CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH}
            title={customerPaymentProviderTitle(CUSTOMER_BANK_TRANSFER_PROVIDER_ID)}
          />
          <h2 className="text-lg font-semibold">{t('qrPayment.payInBankApp')}</h2>
        </div>
        {resolvedCurrency !== null ? (
          <p
            className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
            data-testid="customer-qr-payment-amount"
          >
            {(resolvedAmountMinor / 100).toFixed(2)} {resolvedCurrency}
          </p>
        ) : (
          <p
            className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
            role="status"
            data-testid="customer-qr-payment-amount-unavailable"
          >
            {t('checkout.loadingOrder')}
          </p>
        )}
        {showQrPanel ? (
          <div className="flex w-full min-w-0 max-w-full flex-col items-center gap-[var(--vcl-space-stack-md)]">
            {qrImageUrl !== null ? (
              <img
                src={qrImageUrl}
                alt={t('qrPayment.qrAlt')}
                data-testid="customer-qr-box"
                className="aspect-square h-auto w-full min-w-0 max-w-[280px] rounded-[var(--vcl-control-radius-md)] border border-[var(--color-border)] object-contain"
              />
            ) : (
              <>
                <div
                  data-testid="customer-qr-box"
                  aria-hidden={qrLoadPhase !== 'error'}
                  role={qrLoadPhase === 'error' ? 'status' : undefined}
                  className="flex aspect-square w-full min-w-0 max-w-[280px] items-center justify-center rounded-[var(--vcl-control-radius-md)] border border-dashed border-[var(--color-border)] text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
                >
                  {qrLoadPhase === 'error'
                    ? t('qrPayment.qrLoadError')
                    : t('qrPayment.generatingQr')}
                </div>
                {qrLoadPhase === 'error' ? (
                  <Button
                    intent="secondary"
                    size="sm"
                    type="button"
                    onClick={retryQrImageLoad}
                    data-testid="customer-qr-retry-load"
                  >
                    {t('qrPayment.retryLoad')}
                  </Button>
                ) : null}
              </>
            )}
            {resolvedOrderNumber !== null ? (
              <div
                className="flex w-full min-w-0 max-w-[280px] flex-col items-center gap-[var(--vcl-space-stack-sm)] text-center"
                data-testid="customer-qr-order-number"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-on-surface-muted)]">
                  {t('qrPayment.orderNumberLabel')}
                </p>
                <p className="text-xl font-semibold tabular-nums text-[var(--color-on-surface)]">
                  {t('qrPayment.orderNumberValue', { id: resolvedOrderNumber })}
                </p>
                <p className="text-xs text-[var(--color-on-surface-muted)]">
                  {t('qrPayment.orderNumberHint')}
                </p>
                <Button
                  intent="secondary"
                  size="sm"
                  type="button"
                  onClick={handleCopyOrderNumber}
                  data-testid="customer-qr-order-number-copy"
                >
                  {t('qrPayment.copyOrderNumber')}
                </Button>
              </div>
            ) : null}
            {qrImageUrl !== null ? (
              <Button
                intent="primary"
                size="lg"
                block
                type="button"
                loading={shareBusy}
                disabled={shareBusy}
                onClick={handleSaveOrShare}
              >
                {t('qrPayment.downloadQr')}
              </Button>
            ) : (
              <Button intent="primary" size="lg" block disabled>
                {t('qrPayment.downloadQr')}
              </Button>
            )}
          </div>
        ) : (
          <div
            className="w-full rounded-[var(--vcl-control-radius-md)] border border-[var(--color-border)] p-4 text-left"
            role="status"
          >
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {t('qrPayment.qrNotAvailable')}
            </p>
          </div>
        )}
        {qrPreviewOpen && qrPreviewImageUrl !== null ? (
          <div
            className={[
              'fixed inset-0 z-50 flex flex-col bg-[var(--color-surface)]',
              // Under shell: clear fixed CustomerShellHeader (z-[60]) so preview title/close stay visible.
              underShellChrome ? CUSTOMER_TOP_BAR_CLEARANCE_PT_CLASS : null,
            ]
              .filter(Boolean)
              .join(' ')}
            role="dialog"
            aria-modal="true"
            aria-labelledby="customer-qr-preview-title"
            data-testid="customer-qr-save-preview"
          >
            <div className="flex items-center justify-between gap-[var(--vcl-gap-inline-md)] border-b border-[var(--color-border)] px-4 py-3">
              <h3
                id="customer-qr-preview-title"
                className="text-base font-semibold text-[var(--color-on-surface)]"
              >
                {t('qrPayment.previewTitle')}
              </h3>
              <Button intent="ghost" size="sm" type="button" onClick={closeQrPreview}>
                {t('qrPayment.previewClose')}
              </Button>
            </div>
            <div className="flex flex-1 flex-col items-center justify-center gap-[var(--vcl-space-stack-lg)] overflow-y-auto p-4">
              <img
                src={qrPreviewImageUrl}
                alt={t('qrPayment.qrAlt')}
                className="max-h-[70vh] w-auto min-w-0 max-w-full rounded-[var(--vcl-control-radius-md)] border border-[var(--color-border)]"
              />
              <p className="max-w-sm text-center text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
                {t('qrPayment.longPressSaveInstruction')}
              </p>
              {showDesktopPngDownload ? (
                <Button
                  intent="secondary"
                  size="md"
                  type="button"
                  onClick={handleDesktopDownload}
                >
                  {t('qrPayment.downloadDesktop')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
        {showQrPanel ? (
          <div className="w-full text-left">
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface)]">
              {t('qrPayment.bankAppInstructions')}
            </p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              <li>{t('qrPayment.stepDownloadQr')}</li>
              <li>{t('qrPayment.stepOpenBankApp')}</li>
              <li>{t('qrPayment.stepSelectFromGallery')}</li>
            </ol>
          </div>
        ) : null}
        {showQrPanel ? (
          <div className="w-full rounded-[var(--vcl-control-radius-md)] border border-[var(--color-border)] p-3 text-left">
            {isChecking ? (
              <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
                {t('qrPayment.checkingBank')}
              </p>
            ) : (
              <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
                {t('qrPayment.nextCheckIn', { seconds: timeUntilNextCheck })}
              </p>
            )}
          </div>
        ) : null}
        {downloadHint !== null ? (
          <p className="text-xs text-[var(--color-on-surface-muted)]" role="status">
            {downloadHint}
          </p>
        ) : null}
        {showQrPanel && manualCheck.checkPhase === 'idle' ? (
          <Button
            intent="secondary"
            size="lg"
            block
            onClick={manualCheck.onAssertPaidClick}
            disabled={manualCheck.isCheckingNow}
          >
            {manualCheck.isCheckingNow ? t('qrPayment.verifyingPayment') : t('qrPayment.paidJustNow')}
          </Button>
        ) : null}
        {showQrPanel &&
        (manualCheck.checkPhase === 'polling' || manualCheck.checkPhase === 'completing') ? (
          <div
            className="w-full rounded-[var(--vcl-control-radius-md)] border border-[var(--color-border)] p-3 text-left"
            role="status"
            aria-live="polite"
          >
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {manualCheck.checkPhase === 'completing'
                ? t('qrPayment.waitIdentify')
                : manualCheckMessage ?? t('qrPayment.verifyingPayment')}
            </p>
          </div>
        ) : null}
        {showQrPanel && manualCheck.checkPhase === 'exhausted' ? (
          <div className="w-full flex flex-col gap-[var(--vcl-space-stack-md)] text-left">
            <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]" role="status" aria-live="polite">
              {manualCheckMessage ?? t('qrPayment.refund_promise')}
            </p>
            <RefundRequestCta
              paymentId={paymentId}
              refundEligible={refundEligible}
              {...(paymentApiTenant !== null ? { apiTenantCode: paymentApiTenant } : {})}
            />
            {showRecoveryLinked ? (
              <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-success)]">{t('qrPayment.recoveryLinked')}</p>
            ) : null}
            {linkAccount.isError ? (
              <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-danger)]" role="alert">
                {isTransactionLinkedToOtherCustomerError(linkAccount.error)
                  ? t('qrPayment.recoveryLinkedConflict')
                  : toCustomerFacingErrorMessage(linkAccount.error, {
                      fallback: t('errors.generic'),
                    })}
              </p>
            ) : null}
            {!showRecoveryLinked ? (
              <div className="flex flex-col gap-[var(--vcl-space-stack-sm)]">
                {(() => {
                  if (signedInChrome) {
                    return (
                      <Button
                        intent="primary"
                        size="md"
                        block
                        onClick={() => {
                          handleLinkRecovery();
                        }}
                        disabled={linkAccount.isPending}
                      >
                        {linkAccount.isPending
                          ? t('qrPayment.recoveryLinking')
                          : t('qrPayment.recoveryLinkPayment')}
                      </Button>
                    );
                  }
                  if (canMountSignIn) {
                    return (
                      <Button
                        intent="primary"
                        size="md"
                        block
                        onClick={openRecoverySignIn}
                      >
                        {t('qrPayment.recoverySignIn')}
                      </Button>
                    );
                  }
                  return <SignedInAccountPromptCard />;
                })()}
              </div>
            ) : null}
          </div>
        ) : null}
        {showQrPanel ? (
          <p className="text-xs text-[var(--color-on-surface-muted)]">
            {t('qrPayment.autoRefreshHint')}
          </p>
        ) : null}
        {onCancel !== undefined ? (
          <Button intent="ghost" size="sm" onClick={handleCancel}>
            {t('qrPayment.cancel')}
          </Button>
        ) : null}
      </div>
    </Card>
    </div>
  );
}
